import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { of } from 'rxjs';
import { routeNames } from 'src/app/app-routing-names';
import { AuthType } from 'src/app/models/enums/auth-type';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { QuoteSteps } from '../models/enums/quote-steps';
import { HouseholdQuoteStateService } from '../services/household-quote-state-service/household-quote-state.service';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationGuard {
    constructor(private authenticationService: AuthenticationService,
        private householdQuoteStateService: HouseholdQuoteStateService,
        private router: Router) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.isAllowedNavigate(route, state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.canActivate(route, state);
    }

    async isAllowedNavigate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {

        let hasValidAuth = this.authenticationService.hasAuthToken();
        const authTime = this.authenticationService.getAuthTime();
        const authType = this.authenticationService.getAuthType()
        const currentStep = this.householdQuoteStateService.getCurrentStep();

        if (hasValidAuth && authType === undefined) {
            this.authenticationService.setAuthType(AuthType.Internal);
        }

        // If the token is from the day before and the user is marked internal
        // invalidate the auth and force the user to the login page to avoid and error message being shown
        if (hasValidAuth && authTime &&
            authType === AuthType.Internal &&
            !this.datesAreOnSameDay(new Date(authTime))) {
            hasValidAuth = false
        }

        if (hasValidAuth && !this.authenticationService.hasValidAuthTime() && currentStep <= QuoteSteps.yourQuote) {
            this.authenticationService.getAuthTokenForExternal()
        }

        if (hasValidAuth && (state.url.includes(routeNames.LOGIN) && authType !== AuthType.External)) {
            this.authenticationService.setLoginType(AuthType.Internal);
            if (route.queryParams) {
                this.router.navigateByUrl(route.queryParams.return);
            } else {
                this.householdQuoteStateService.setStepState(QuoteSteps.ownPropertyQuestion);
            }
        } else if (!hasValidAuth && !state.url.includes(routeNames.LOGIN)) {
            this.authenticationService.setLoginType();
            this.router.navigate([routeNames.LOGIN], {
                queryParams: {
                    return: state.url
                }
            });
        } else if (!hasValidAuth && state.url.includes(routeNames.LOGIN)) {
            return of(true).toPromise();
        }

        if (!state.url.includes(routeNames.LOGIN) && currentStep < QuoteSteps.yourQuote) {
            this.authenticationService.setLoginType(authType);
            this.authenticationService.monitorForActivityEvents();
        } else {
            this.authenticationService.setLoginType();
            this.authenticationService.unsubscribeFromActivitySub();
        }
        return of(hasValidAuth).toPromise();
    }

    private datesAreOnSameDay(first: Date, second: Date = new Date()): boolean {
        return first.getFullYear() === second.getFullYear() &&
            first.getMonth() === second.getMonth() &&
            first.getDate() === second.getDate();
    }
}
